import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "rivaaz_cart_v2";

const itemKey = (it) => `${it.item_id}__${it.variant_name || ""}__${it.spice_level || ""}__${it.dip || ""}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (item) => {
    if (!item.item_id) {
      throw new Error("Cart items must include item_id");
    }
    setItems((prev) => {
      const k = itemKey(item);
      const idx = prev.findIndex((p) => itemKey(p) === k);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
        return next;
      }
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
    // Note: the cart drawer does NOT auto-open on add — customers complained
    // it was disruptive. They open it themselves via the cart icon in the
    // header. Confirmation still happens via a toast in the calling page.
  };

  const remove = (item) => {
    setItems((prev) => prev.filter((p) => itemKey(p) !== itemKey(item)));
  };

  const updateQty = (item, qty) => {
    if (qty <= 0) return remove(item);
    setItems((prev) =>
      prev.map((p) => (itemKey(p) === itemKey(item) ? { ...p, quantity: qty } : p))
    );
  };

  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, p) => s + p.price * p.quantity, 0);
    const count = items.reduce((s, p) => s + p.quantity, 0);
    return { subtotal: Math.round(subtotal * 100) / 100, count };
  }, [items]);

  const value = { items, add, remove, updateQty, clear, open, setOpen, ...totals };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
